import { Timestamp } from '@angular/fire/firestore';

/** Pipeline stages — see .doc/roadmap-crm.md §4 */
export type CrmLeadStatus =
  | 'identified'
  | 'contacted'
  | 'qualified'
  | 'committed'
  | 'registered'
  | 'draft'
  | 'live'
  | 'activated'
  | 'lost'
  | 'nurturing';

export type CrmLeadPriority = 'A' | 'B' | 'C';

export type CrmVenueType = 'sala_fiestas' | 'coworking' | 'estudio' | 'otro';

export type CrmLeadSource =
  | 'maps'
  | 'instagram'
  | 'referral'
  | 'inbound'
  | 'fair'
  | 'other'
  | 'landing';

export type CrmActivityType = 'call' | 'whatsapp' | 'email' | 'meeting' | 'note' | 'status_change';

export type CrmVenueSource = 'maps' | 'manual';

/** A physical location linked to a contact/lead (subcollection `/venues`). */
export interface CrmVenue {
  id: string;
  venueName: string;
  venueType: CrmVenueType;
  city: string;
  province?: string;
  mapsUrl?: string;
  placeId?: string;
  formattedAddress?: string;
  website?: string;
  source: CrmVenueSource;
  spaceId?: string;
  createdAt: Timestamp;
}

export interface CrmLead {
  id: string;
  status: CrmLeadStatus;
  priority: CrmLeadPriority;
  /** Denormalized primary local for list/kanban (synced from venues). */
  venueName: string;
  venueType: CrmVenueType;
  city: string;
  province?: string;
  mapsUrl?: string;
  /** Number of linked venues (denormalized). */
  venueCount?: number;
  website?: string;
  instagram?: string;
  contactName?: string;
  phone?: string;
  /** Digits-only key for deduplication (see normalizePhoneForMatch). */
  phoneNormalized?: string;
  email?: string;
  source: CrmLeadSource;
  sourceDetail?: string;
  estimatedHourlyEur?: number;
  competitor?: string;
  notes?: string;
  nextAction: string;
  nextActionAt: Timestamp;
  lastContactAt?: Timestamp;
  lostReason?: string;
  ownerUid?: string;
  spaceId?: string;
  referralBookingId?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CrmActivity {
  id: string;
  leadId: string;
  type: CrmActivityType;
  body: string;
  createdAt: Timestamp;
  createdBy: string;
}

export const CRM_LEAD_STATUSES: CrmLeadStatus[] = [
  'identified',
  'contacted',
  'qualified',
  'committed',
  'registered',
  'draft',
  'live',
  'activated',
  'lost',
  'nurturing',
];

export const CRM_STATUS_LABELS: Record<CrmLeadStatus, string> = {
  identified: 'Identificado',
  contacted: 'Contactado',
  qualified: 'Cualificado',
  committed: 'Compromiso',
  registered: 'Registrado',
  draft: 'Borrador espacio',
  live: 'Publicado',
  activated: 'Activado',
  lost: 'Perdido',
  nurturing: 'Nurturing',
};

export const CRM_STATUS_COLORS: Record<CrmLeadStatus, string> = {
  identified: 'bg-slate-100 text-slate-700',
  contacted: 'bg-sky-100 text-sky-800',
  qualified: 'bg-indigo-100 text-indigo-800',
  committed: 'bg-violet-100 text-violet-800',
  registered: 'bg-amber-100 text-amber-900',
  draft: 'bg-orange-100 text-orange-900',
  live: 'bg-emerald-100 text-emerald-800',
  activated: 'bg-green-100 text-green-900',
  lost: 'bg-red-100 text-red-800',
  nurturing: 'bg-stone-100 text-stone-700',
};

export const CRM_VENUE_TYPE_LABELS: Record<CrmVenueType, string> = {
  sala_fiestas: 'Sala de fiestas',
  coworking: 'Coworking',
  estudio: 'Estudio',
  otro: 'Otro',
};

export const CRM_SOURCE_LABELS: Record<CrmLeadSource, string> = {
  maps: 'Google Maps',
  instagram: 'Instagram',
  referral: 'Referido',
  inbound: 'Inbound',
  fair: 'Feria / evento',
  other: 'Otro',
  landing: 'Landing web',
};

export const CRM_ACTIVITY_TYPE_LABELS: Record<CrmActivityType, string> = {
  call: 'Llamada',
  whatsapp: 'WhatsApp',
  email: 'Email',
  meeting: 'Reunión',
  note: 'Nota',
  status_change: 'Cambio de etapa',
};

/** Active pipeline (excludes terminal / pause buckets unless filtered). */
export const CRM_ACTIVE_STATUSES: CrmLeadStatus[] = [
  'identified',
  'contacted',
  'qualified',
  'committed',
  'registered',
  'draft',
  'live',
  'activated',
];

/** Simplified labels for the Leads table (crm.png mockup). */
export const CRM_TABLE_STATUS_LABELS: Partial<Record<CrmLeadStatus, string>> = {
  identified: 'PENDIENTE',
  contacted: 'CONTACTADO',
  qualified: 'INTERESADO',
  committed: 'INTERESADO',
  registered: 'INTERESADO',
  draft: 'ONBOARDING',
  live: 'ACTIVO',
  activated: 'ACTIVO',
  lost: 'BLOQUEADO',
  nurturing: 'NURTURING',
};

export const CRM_PRIORITY_LABELS: Record<CrmLeadPriority, string> = {
  A: 'Crítica',
  B: 'Alta',
  C: 'Media',
};

export const CRM_PRIORITY_COLORS: Record<CrmLeadPriority, string> = {
  A: 'bg-midnight-indigo text-white',
  B: 'bg-red-100 text-red-800',
  C: 'bg-amber-100 text-amber-900',
};

/** Kanban columns for the Pipelines tab (deals.png). */
export interface CrmPipelineColumn {
  id: string;
  label: string;
  statuses: CrmLeadStatus[];
  headerClass: string;
}

export function crmLeadVenueCount(lead: CrmLead, venues?: CrmVenue[]): number {
  if (venues?.length) return venues.length;
  return lead.venueCount ?? (lead.venueName ? 1 : 0);
}

export function crmLeadDisplayVenueName(lead: CrmLead, venues?: CrmVenue[]): string {
  if (venues?.length) return venues[0].venueName;
  return lead.venueName || 'Sin local';
}

export const CRM_PIPELINE_COLUMNS: CrmPipelineColumn[] = [
  {
    id: 'new',
    label: 'Nuevo',
    statuses: ['identified'],
    headerClass: 'bg-violet-600 text-white',
  },
  {
    id: 'contact',
    label: 'Contacto',
    statuses: ['contacted', 'qualified'],
    headerClass: 'bg-blue-600 text-white',
  },
  {
    id: 'commit',
    label: 'Compromiso',
    statuses: ['committed', 'registered'],
    headerClass: 'bg-cyan-600 text-white',
  },
  {
    id: 'onboard',
    label: 'Onboarding',
    statuses: ['draft'],
    headerClass: 'bg-teal-600 text-white',
  },
  {
    id: 'won',
    label: 'Activo',
    statuses: ['live', 'activated'],
    headerClass: 'bg-emerald-600 text-white',
  },
];
